<template>
  <button
    class="relative h-10 w-10 ml-1 focus:outline-none"
    v-bind="$attrs"
    @wheel="onWheel"
    @mouseover="showPopup"
    @touchstart="showPopup"
    @mouseout="hidePopup"
    @blur="hidePopup"
  >
    <transition-fade>
      <span
        v-show="popupVisible || dragging"
        class="group absolute bottom-0 left-10 block w-10 h-48 pb-12 -translate-x-[38px] z-10"
      >
        <span
          @click="set"
          @mousedown="dragStart"
          @touchstart="touchStart"
          class="flex flex-col items-center h-full w-full px-2 py-[1.125rem] rounded-full rounded-bl-none bg-school-level-volume text-player-control-default"
        >
          <span
            ref="slider"
            class="flex flex-col justify-end w-[1px] h-full bg-player-volume-line-control-default transition-colors duration-200"
          >
            <span
              class="relative block w-full duration-200"
              :class="[
                { 'bg-opacity-25': muted },
                // Transition `height` only on click, but not while dragging.
                // A `height` transition while dragging creates an undesirable lagging effect.
                dragging ? 'transition-colors' : 'transition-all',
              ]"
              :style="{ height: `${volume}%` }"
            >
              <span
                class="absolute top-0 left-1/2 -translate-y-1/2 -translate-x-1/2 block w-4 h-4 bg-player-volume-control-default rounded-full"
              />
            </span>
          </span>
        </span>
      </span>
    </transition-fade>
    <span class="absolute inset-0 block z-10" ref="mute">
      <inline-svg
        v-if="muted"
        :src="require('url:./icons/volume/volume-mute.svg')"
        class="w-full h-full p-[0.65rem]"
      />
      <inline-svg
        v-else-if="volume < 25"
        :src="require('url:./icons/volume/volume-off.svg')"
        class="w-full h-full p-[0.65rem]"
      />
      <inline-svg
        v-else-if="volume < 50"
        :src="require('url:./icons/volume/volume-down.svg')"
        class="w-full h-full p-[0.65rem]"
      />
      <inline-svg
        v-else-if="volume < 75"
        :src="require('url:./icons/volume/volume.svg')"
        class="w-full h-full p-[0.65rem]"
      />
      <inline-svg
        v-else
        :src="require('url:./icons/volume/volume-up.svg')"
        class="w-full h-full p-[0.65rem]"
      />
    </span>
  </button>

  <!-- Preload all icons to prevent visible network lag when changing the volume. -->
  <ul class="sr-only">
    <li><inline-svg :src="require('url:./icons/volume/volume-mute.svg')" /></li>
    <li><inline-svg :src="require('url:./icons/volume/volume-off.svg')" /></li>
    <li><inline-svg :src="require('url:./icons/volume/volume-down.svg')" /></li>
    <li><inline-svg :src="require('url:./icons/volume/volume.svg')" /></li>
    <li><inline-svg :src="require('url:./icons/volume/volume-up.svg')" /></li>
  </ul>
</template>

<script>
import InlineSvg from "vue-inline-svg";

import TransitionFade from "../../../../magic/TransitionFade.vue";

export default {
  name: "VolumeControl",
  components: {
    InlineSvg,
    TransitionFade,
  },
  inheritAttrs: false,
  data() {
    return {
      popupVisible: false,
      dragging: false,
      closeDelay: 100,
      closeTimeout: null,
      allowMuteDelay: 50,
      allowMuteTimeout: null,
    };
  },
  props: {
    volume: {
      type: Number,
      default: 75,
      validator: (volume) => volume >= 0 && volume <= 100,
    },
    muted: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    toggleMuted() {
      this.$emit("update:muted", !this.muted);
    },
    onWheel(event) {
      // Change volume by mouse wheel while hovering the volume control.
      // Clamp the volume to values from 0 to 100.
      const volume = Math.max(0, Math.min(this.volume + event.deltaY / 5, 100));
      this.$emit("update:volume", volume);
    },
    showPopup({ type }) {
      const touch = type === "touchstart";

      // Show the popup. If it is currently waiting to close, cancel the wait.
      if (this.closeTimeout) {
        window.clearTimeout(this.closeTimeout);
      }

      if (!this.popupVisible) {
        // Enable click to toggle mute only after a delay.
        // This way we can allow touch devices (digibord
        // screens) to open and close the popup via
        // touch, without accidentally muting.
        this.allowMuteTimeout = window.setTimeout(
          // Note: `this.$refs.mute` is null when navigating away while timeout passes.
          () =>
            this.$refs.mute?.addEventListener(
              touch ? "touchstart" : "click",
              this.toggleMuted,
            ),
          this.allowMuteDelay,
        );
      }

      this.popupVisible = true;
    },
    hidePopup() {
      // Hide the popup after waiting for a moment,
      // for improved UX and flicker-free display.
      const vm = this;
      this.closeTimeout = window.setTimeout(() => {
        // Note: `this.$refs.mute` is null when navigating away while timeout passes.
        this.$refs.mute?.removeEventListener("click", this.toggleMuted);
        this.$refs.mute?.removeEventListener("touchstart", this.toggleMuted);

        if (this.allowMuteTimeout) {
          window.clearTimeout(this.allowMuteTimeout);
        }

        vm.popupVisible = false;
      }, this.closeDelay);
    },
    set(event) {
      const clientY =
        event.type === "touchmove" ? event.touches[0].clientY : event.clientY;
      // Set the volume via a click or mouse move (if currently dragging) event,
      // by inspecting the event's coordinates in respect to the slider's bounding rect.
      const boundingBox = this.$refs.slider.getBoundingClientRect();
      let volume = 100 - ((clientY - boundingBox.y) / boundingBox.height) * 100;
      // Clamp the volume to values from 0 to 100.
      volume = Math.max(0, Math.min(volume, 100));
      this.$emit("update:volume", volume);
    },
    dragStart() {
      // Start mouse down + move dragging to freely adjust the volume.
      this.dragging = true;
      document.addEventListener("mousemove", this.drag);
      document.addEventListener("mouseup", this.dragEnd, { once: true });
    },
    touchStart(event) {
      if (event.touches.length > 1) {
        return;
      }

      // Start mouse down + move dragging to freely adjust the volume.
      this.dragging = true;
      document.addEventListener("touchmove", this.drag);
      document.addEventListener("touchend", this.dragEnd, { once: true });
    },
    drag(event) {
      // Use the mouse move event to set the volume,
      // if we are currently dragging (= after mouse down).
      if (!this.dragging) {
        return;
      }
      this.set(event);
    },
    dragEnd() {
      // End dragging with the mouse up event.
      // Cancel the mouse move listener and stop drag-mode.
      document.removeEventListener("mousemove", this.drag);
      document.removeEventListener("touchmove", this.drag);
      this.dragging = false;
    },
  },
};
</script>
